import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import LoggedAdmin from '../../../Api/Models/LoggedAdmin';
import useGlobalState from '../../GlobalStateContext';
import Auth from '../../services/Auth';

import { saveCRMCredentials } from 'crm/services/Storage';

const isCaptchaEnabled = process.env.MIX_CAPTCHA_CRM_LOGIN_ENABLED?.toLowerCase() === 'true';
const reCaptchaV3Key = process.env.MIX_CAPTCHA_V3_SITE_KEY || '';
const reCaptchaV2Key = process.env.MIX_CAPTCHA_V2_SITE_KEY || '';
const useCaptchaV3 = !!reCaptchaV3Key;
const useCaptchaV2 = !useCaptchaV3 && !!reCaptchaV2Key;

export default function LoginForm() {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [captcha, setCaptcha] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [error, setError] = useState(false);
    const [captchaError, setCaptchaError] = useState(false);
    const history = useHistory();
    const { setLoggedAdmin } = useGlobalState();

    useEffect(() => {
        if (!isCaptchaEnabled) return;

        if (useCaptchaV3) {
            if (document.querySelector('script[src*="recaptcha/api.js"]')) return;

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${reCaptchaV3Key}`;
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);

            script.onload = () => {
                if (!window.grecaptcha?.ready) return;
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(reCaptchaV3Key, { action: 'crm_login' }).then(setCaptcha);
                });
            };
        } else if (useCaptchaV2) {
            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                window.handleReCaptchaChange = (token) => setCaptcha(token);
            };
            document.body.appendChild(script);
        }
    }, []);

    const regenerateRecaptchaToken = () => {
        if (useCaptchaV3) {
            if (window.grecaptcha?.ready) {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(reCaptchaV3Key, { action: 'crm_login' }).then(setCaptcha);
                });
            } else {
                console.warn('[Captcha] reCAPTCHA v3 not ready');
            }
        }

        if (useCaptchaV2) {
            try {
                if (window.grecaptcha?.reset && typeof window.grecaptcha.reset === 'function') {
                    window.grecaptcha.reset();
                } else {
                    setTimeout(() => {
                        try {
                            if (window.grecaptcha?.reset && typeof window.grecaptcha.reset === 'function') {
                                window.grecaptcha.reset();
                            }
                        } catch (e) {
                            console.warn('[Captcha] Delayed reset error:', e);
                        }
                    }, 300);
                }
            } catch (e) {
                console.warn('[Captcha] Immediate reset error:', e);
            }
        }
    };

    const setInvalidCredentialsError = () => {
        setErrorMessage('Your login credentials could not be verified, please try again.');
        setError(true);
    };

    const loginHandler = (event) => {
        event.preventDefault();
        Auth.authenticate(login, password, captcha)
            .then((response) => {
                setErrorMessage('');

                switch (response.status) {
                    case 200:
                        return response.json();

                    case 400:
                        response.json().then((errorData) => {
                            console.warn('[Login] 400 error:', errorData);
                            if (errorData.error === 'invalid_captcha') {
                                setCaptcha('');
                                setCaptchaError(true);
                                setErrorMessage('Invalid CAPTCHA. Please try again.');
                                regenerateRecaptchaToken();
                            } else {
                                setInvalidCredentialsError();
                            }
                        });
                        break;

                    case 403:
                        setErrorMessage('You have no access to the CRM.');
                        setError(true);
                        break;

                    case 429:
                        setErrorMessage('Too many attempts!');
                        setError(true);
                        break;

                    default:
                        setInvalidCredentialsError();
                        break;
                }

                return null;
            })
            .then((body) => {
                if (body === null) {
                    return;
                }

                setError(false);
                setCaptchaError(false);

                saveCRMCredentials(body.access_token, body.refresh_token, body.token_type, body.expires_in);

                setLoggedAdmin(new LoggedAdmin(body.admin));
                history.push('/profile');
            });
    };

    return (
        <div className="main-wrapper">
            <div
                className="auth-wrapper d-flex no-block justify-content-center align-items-center"
                style={{
                    backgroundImage: "url('/images/crm/auth-bg.jpg')",
                    backgroundPosition: 'center',
                }}
            >
                <div className="auth-box">
                    <div id="loginform">
                        <div className="logo">
                            <span className="db">
                                <img src="/images/crm/logo-icon.png" alt="logo" />
                            </span>
                            <h5 className="font-medium m-b-20">Login Page</h5>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <form onSubmit={loginHandler}>
                                    <div className="input-group mb-3">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text" id="basic-addon1">
                                                <i className="ti-user" />
                                            </span>
                                        </div>
                                        <input
                                            type="text"
                                            name="username"
                                            className={`form-control form-control-lg ${error && 'is-invalid'}`}
                                            onChange={(e) => {
                                                setLogin(e.target.value);
                                                setError(false);
                                            }}
                                            placeholder="login"
                                            aria-label="login"
                                            aria-describedby="basic-addon1"
                                            required
                                        />
                                    </div>
                                    <div className="input-group mb-3">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text" id="basic-addon2">
                                                <i className="ti-pencil" />
                                            </span>
                                        </div>
                                        <input
                                            type="password"
                                            name="password"
                                            className={`form-control form-control-lg ${error && 'is-invalid'}`}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setError(false);
                                            }}
                                            placeholder="password"
                                            aria-label="password"
                                            aria-describedby="basic-addon1"
                                            autoComplete="do-not-autofill"
                                            required
                                        />
                                    </div>
                                    {isCaptchaEnabled && useCaptchaV2 && (
                                        <div className="input-group mb-3">
                                            <div
                                                className={`g-recaptcha ${captchaError && 'is-invalid'}`}
                                                data-sitekey={reCaptchaV2Key}
                                                data-callback="handleReCaptchaChange"
                                            />
                                        </div>
                                    )}
                                    <div className="form-group row">
                                        <div className="col-md-12">
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id="customCheck1"
                                                />
                                                <label className="custom-control-label" htmlFor="customCheck1">
                                                    Remember me
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {errorMessage && (
                                        <div className="alert alert-danger" role="alert">
                                            {errorMessage}
                                        </div>
                                    )}

                                    <div className="form-group text-center">
                                        <div className="col-xs-12 p-b-20">
                                            <button type="submit" className="btn btn-block btn-lg btn-info">
                                                Sign In
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
